import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { Card } from '../ui/Card';
import { Banner } from '../ui/Banner';
import { Spinner } from '../ui/Spinner';
import { SecretBlock } from '../ui/SecretBlock';
import { LinkButton } from '../ui/LinkButton';
import { getSecret, type GetSecretResponse } from '../../lib/api';

type Status =
  | { kind: 'loading' }
  | { kind: 'ready'; data: GetSecretResponse }
  | { kind: 'expired'; description: string }
  | { kind: 'error'; message: string };

const inFlight = new Map<string, Promise<GetSecretResponse>>();

export default function ReadSecret() {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const request = inFlight.get(id) ?? getSecret(id);
    if (!inFlight.has(id)) inFlight.set(id, request);
    request
      .then((data) => {
        if (cancelled) return;
        if (data.secretFound) {
          setStatus({ kind: 'ready', data });
        } else {
          setStatus({ kind: 'expired', description: data.description || 'Link expired' });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus({
          kind: 'error',
          message:
            err instanceof Error ? err.message : 'Something went wrong loading this secret.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (status.kind === 'loading') {
    return (
      <Card>
        <div className="flex flex-col items-center gap-4 py-6">
          <Spinner size={28} className="text-primary" />
          <p className="font-mono text-sm text-text-muted">Retrieving secret…</p>
        </div>
      </Card>
    );
  }

  if (status.kind === 'expired') {
    return (
      <Card>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-text">
          {status.description || 'Link expired'}
        </h1>
        <p className="mt-3 text-sm text-text-muted sm:text-base">
          This link has already been used or expired. Secrets vanish after one read or 10 minutes.
        </p>
        <div className="mt-6">
          <LinkButton to="/" fullWidth leadingIcon={<Sparkles size={18} />}>
            Send a new secret
          </LinkButton>
        </div>
      </Card>
    );
  }

  if (status.kind === 'error') {
    return (
      <Card>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-text">
          Couldn&rsquo;t load this secret
        </h1>
        <Banner tone="danger" icon={<AlertCircle size={16} />} className="mt-5">
          {status.message}
        </Banner>
        <div className="mt-6">
          <LinkButton to="/" fullWidth>
            Send a new secret
          </LinkButton>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-text sm:text-[2rem]">
        Your one-time message
      </h1>
      <p className="mt-3 text-sm text-text-muted">
        Copy it now &mdash; it&rsquo;s already been deleted from our servers.
      </p>
      <div className="mt-6">
        <SecretBlock
          value={status.data.message}
          onCopy={() => toast.success('Secret copied to clipboard')}
        />
      </div>
      <Banner tone="success" icon={<CheckCircle2 size={16} />} className="mt-4">
        This secret has been destroyed. It can&rsquo;t be viewed again.
      </Banner>
      <div className="mt-6">
        <LinkButton to="/" fullWidth>
          Send your own
        </LinkButton>
      </div>
    </Card>
  );
}
